import {
    AddressAndContact,
    MeansOfTransport,
    TransportAssignment,
    TransportAssignmentStatus,
    TransportAssignmentType,
} from '../../transportAssignment.types';
import { TransportAssignmentConfirmationFormState, TransportAssignmentData } from './confirmationForm.types';
import {
    DELIVERY_DATE_VALIDATION_MESSAGES,
    getEmptyValidationResult,
    keepFirstValidationMessage,
    PICKUP_DATE_VALIDATION_MESSAGES,
    validateDeliveryDate,
    validateMeansOfTransportLength,
    validatePickUpDate,
    validateTrailerIdLength,
} from '../../shared/sharedFormValidations';
import { isDateStringEmpty } from '../../../sharedComponents/dateHelper';
import { ValidationResult } from '../../../sharedComponents/validationResult.type';

type TransportAssignmentConfirmationFormValidationResult = {
    confirmedPickUpDate: ValidationResult;
    confirmedDeliveryDate: ValidationResult;
    meansOfTransport: ValidationResult;
    unloadingAddress: ValidationResult;
    trailer: ValidationResult;
    isButtonDisabled: boolean;
};

export const isFormDisabled = (transportAssignment: TransportAssignment) =>
    transportAssignment.isFinalized || transportAssignment.status === TransportAssignmentStatus.CANCELLATION;

export const validateIncompleteMeansOfTransport = (
    enabled: boolean,
    meansOfTransport: MeansOfTransport | undefined,
): ValidationResult =>
    enabled && (meansOfTransport?.id === undefined || meansOfTransport.id.length === 0)
        ? {
              error: undefined,
              warning:
                  'outboundOrderBook.transportAssignments.sidebar.confirmationForm.meansOfTransport.feedbackWarning',
          }
        : getEmptyValidationResult();

const validateIncompleteUnloadingAddress = (
    enabled: boolean,
    transportAssignment: TransportAssignment,
    unloadingAddress: AddressAndContact | undefined,
): ValidationResult =>
    enabled && transportAssignment.type === TransportAssignmentType.TRANSPORT_ORDER && unloadingAddress === undefined
        ? {
              error: undefined,
              warning:
                  'outboundPortal.transportAssignments.sidebar.assignmentInformation.unloadingAddress.feedbackWarning',
          }
        : getEmptyValidationResult();

export const validateIncompletePickUpDate = (
    enabled: boolean,
    confirmedPickUpDate: Date | string | undefined,
): ValidationResult =>
    enabled && isDateStringEmpty(confirmedPickUpDate)
        ? { warning: PICKUP_DATE_VALIDATION_MESSAGES.missing, error: undefined }
        : { error: undefined, warning: undefined };

export const validateIncompleteDeliveryDate = (
    enabled: boolean,
    confirmedDeliveryDate: Date | string | undefined,
): ValidationResult =>
    enabled && isDateStringEmpty(confirmedDeliveryDate)
        ? { warning: DELIVERY_DATE_VALIDATION_MESSAGES.missing, error: undefined }
        : { error: undefined, warning: undefined };

export const validateConfirmationForm = (
    transportAssignment: TransportAssignment,
    transportAssignmentData: TransportAssignmentData,
    state: TransportAssignmentConfirmationFormState,
): TransportAssignmentConfirmationFormValidationResult => {
    const noValidationError: ValidationResult = { error: undefined, warning: undefined };
    if (isFormDisabled(transportAssignment)) {
        return {
            confirmedPickUpDate: noValidationError,
            confirmedDeliveryDate: noValidationError,
            meansOfTransport: noValidationError,
            unloadingAddress: noValidationError,
            trailer: noValidationError,
            isButtonDisabled: true,
        };
    }

    const needsCompleteInput = [
        TransportAssignmentStatus.INCOMPLETE_CONFIRMED,
        TransportAssignmentStatus.COMPLETE_CONFIRMED,
        TransportAssignmentStatus.UNCONFIRMED,
    ].includes(transportAssignment.status);

    const result = {
        confirmedPickUpDate: keepFirstValidationMessage(
            validatePickUpDate(transportAssignmentData.requestedPickUpDate, state.confirmedPickUpDate),
            validateIncompletePickUpDate(needsCompleteInput, state.confirmedPickUpDate),
        ),
        confirmedDeliveryDate: keepFirstValidationMessage(
            validateDeliveryDate(transportAssignmentData, state.confirmedDeliveryDate),
            validateIncompleteDeliveryDate(needsCompleteInput, state.confirmedDeliveryDate),
        ),
        meansOfTransport: keepFirstValidationMessage(
            validateMeansOfTransportLength(state.meansOfTransport),
            validateIncompleteMeansOfTransport(needsCompleteInput, state.meansOfTransport),
        ),
        trailer: validateTrailerIdLength(state.meansOfTransport),
        unloadingAddress: validateIncompleteUnloadingAddress(
            needsCompleteInput,
            transportAssignment,
            state.unloadingAddress,
        ),
    };

    const isButtonDisabled = Boolean(Object.values(result).find((r) => r?.error));

    return { ...result, isButtonDisabled };
};
